import { useState } from 'react'
import { MemoryRouter, useInRouterContext } from 'react-router-dom'

import type { Environment } from '../App'
import type {
  OrderItem,
  OrderStateCard,
  OrderStateCta,
  TimelineLabels,
} from '../components/details/types'
import { OrderListTranslationsProvider, type Translate } from '../i18n'
import { OrderDetailsView } from './details'
import { OrderList } from './list'

interface OrderListContainerProps {
  accountNumber?: string
  env: Environment
  authToken?: string
  /**
   * All `active_orders` cards for the customer, resolved and translated by the
   * host. The detail view filters them down to the order being viewed.
   */
  orderStateCards?: OrderStateCard[]
  onOrderStateClick?: (card: OrderStateCard, cta: OrderStateCta) => void
  /** Translated timeline captions, supplied by the host. */
  timelineLabels?: TimelineLabels
  /**
   * Host translator for this package's own copy. Pass `@susu/i18n`'s `t`
   * straight through — the keys are namespaced `orderList.*`. Omit it and every
   * string falls back to the bundled English, so this is additive.
   */
  t?: Translate
  /**
   * BCP-47 language of the copy `t` returns. Used only to pick a plural
   * category — pass it alongside `t` or counts fall back to English rules.
   */
  language?: string
  /**
   * Full `xx-XX` locale for order dates, e.g. `en-GB`. Only the region gets the
   * field order right and `language` alone carries none. Omit it and the locale
   * is read off the host document (URL path, then `<html lang>`), which is
   * correct on the storefront; pass it when the host knows better.
   */
  locale?: string
  /**
   * Controlled order selection: pass a string to open that order's details,
   * `null` for the list. Omit entirely to let the container manage it.
   */
  selectedOrderId?: string | null
  onSelectedOrderIdChange?: (orderId: string | null) => void
  onEmptyState?: (isEmpty: boolean) => void
  role?: 'customer' | 'style-advisor'
  isCancellationFlowEnabled?: boolean
  onFaqOpen?: (title: string) => void
  onOrderClicked?: (orderId: string) => void
  /** Forwarded to the list: the host already painted its placeholder. */
  skipSkeletonDelay?: boolean
  onReturnClicked?: (orderId: string) => void
  onItemDetailsClick?: (item: OrderItem) => void
  onCancellationClicked?: () => void
  onCancellationConfirmed?: () => void
  onCopyProductCode?: (code: string) => void
}

function OrderListContainerContent({
  accountNumber,
  env,
  authToken,
  orderStateCards,
  onOrderStateClick,
  timelineLabels,
  selectedOrderId: controlledSelectedOrderId,
  onSelectedOrderIdChange,
  onEmptyState,
  role = 'customer',
  isCancellationFlowEnabled = true,
  onFaqOpen,
  onOrderClicked,
  onReturnClicked,
  onItemDetailsClick,
  onCancellationClicked,
  onCancellationConfirmed,
  onCopyProductCode,
  skipSkeletonDelay,
}: OrderListContainerProps) {
  const [internalSelectedOrderId, setInternalSelectedOrderId] = useState<
    string | null
  >(null)
  // `null` is a meaningful value here (the list), so `undefined` is what marks
  // the prop as absent — hence the explicit check rather than `??`.
  const selectedOrderId =
    controlledSelectedOrderId !== undefined
      ? controlledSelectedOrderId
      : internalSelectedOrderId

  const selectOrder = (orderId: string | null) => {
    if (onSelectedOrderIdChange) {
      onSelectedOrderIdChange(orderId)
      return
    }
    setInternalSelectedOrderId(orderId)
  }

  const handleOrderClick = (orderId: string) => {
    onOrderClicked?.(orderId)
    selectOrder(orderId)
  }

  const handleBack = () => {
    selectOrder(null)
  }

  if (selectedOrderId && accountNumber) {
    return (
      <OrderDetailsView
        orderId={selectedOrderId}
        accountNumber={accountNumber}
        env={env}
        orderStateCards={orderStateCards}
        onOrderStateClick={onOrderStateClick}
        timelineLabels={timelineLabels}
        onBack={handleBack}
        role={role}
        isCancellationFlowEnabled={isCancellationFlowEnabled}
        authToken={authToken}
        onFaqOpen={onFaqOpen}
        onReturnClicked={onReturnClicked}
        onItemDetailsClick={onItemDetailsClick}
        onCancellationClicked={onCancellationClicked}
        onCancellationConfirmed={onCancellationConfirmed}
        onCopyProductCode={onCopyProductCode}
      />
    )
  }

  return (
    <OrderList
      authToken={authToken || ''}
      accountNumber={accountNumber}
      env={env}
      onOrderClick={handleOrderClick}
      onEmptyState={onEmptyState}
      skipSkeletonDelay={skipSkeletonDelay}
    />
  )
}

/**
 * Container component that manages view switching between order list and order details
 * without using routes. This is designed for embedding in parent applications.
 *
 * Wraps with MemoryRouter only when no router context exists in the tree, to satisfy
 * useNavigate inside child components while avoiding nested-router crashes.
 */
export function OrderListContainer({
  t,
  language,
  locale,
  ...props
}: OrderListContainerProps) {
  const inRouterContext = useInRouterContext()

  const content = inRouterContext ? (
    <OrderListContainerContent {...props} />
  ) : (
    <MemoryRouter>
      <OrderListContainerContent {...props} />
    </MemoryRouter>
  )

  return (
    <OrderListTranslationsProvider t={t} language={language} locale={locale}>
      {content}
    </OrderListTranslationsProvider>
  )
}
